import { ReactNode, useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useProfile } from "@/hooks/useProfile";
import { useUserRoles } from "@/hooks/useUserRoles";
import { useUnreadMessageCount } from "@/hooks/useMessages";
import { useNewConnectionsIndicator } from "@/hooks/useNewConnectionsIndicator";
import { useAdminNotifications } from "@/hooks/useAdminNotifications";
import { useOnboardingTour } from "@/hooks/useOnboardingTour";
import { useStorageUrl } from "@/hooks/useStorageUrl";
import { useNeedsAttentionCount } from "@/hooks/useStudentAssignments";
import { useUserAssignments } from "@/hooks/useUserAssignments";
import { usePendingReviewCount } from "@/hooks/usePendingReviews";
import { useConnectionNotifications } from "@/hooks/useConnectionNotifications";
import { AdminNotificationDialog } from "@/components/admin/AdminNotificationDialog";
import { OnboardingTour } from "@/components/onboarding/OnboardingTour";
import { SurveyAssignmentModal } from "@/components/surveys/SurveyAssignmentModal";
import { SurveyPendingBanner } from "@/components/surveys/SurveyPendingBanner";
import { AskNedraButton } from "@/components/support/AskNedraButton";
import { SupportPanel } from "@/components/support/SupportPanel";
import { NotificationsBell } from "@/components/notifications/NotificationsBell";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import {
  LayoutDashboard,
  Users,
  UserCircle,
  MessageSquare,
  Calendar,
  Settings,
  LogOut,
  Menu,
  X,
  Shield,
  Megaphone,
  BookOpen,
  GraduationCap,
  ExternalLink,
  Mail,
  Library,
  CalendarDays,
  HelpCircle,
  ClipboardCheck,
  Download,
  Presentation,
  Store,
  KeyRound,
} from "lucide-react";
import { useHelpSettings } from "@/hooks/useHelpCenter";
import { usePitchDeckAccess } from "@/hooks/usePitchDeckAccess";
import { useMyTopShopApplication } from "@/components/topshop/topShopShared";

interface DashboardLayoutProps {
  children: ReactNode;
}

export function DashboardLayout({ children }: DashboardLayoutProps) {
  const { user, signOut } = useAuth();
  const { data: profile, isLoading: profileLoading } = useProfile();
  const { data: roles } = useUserRoles();
  const { data: unreadCount } = useUnreadMessageCount();
  const { data: newIndicator } = useNewConnectionsIndicator();
  const { data: needsAttentionCount } = useNeedsAttentionCount();
  const { data: pendingReviewCount } = usePendingReviewCount();
  const { count: learningAssignmentsCount } = useUserAssignments();
  
  // Combined badge for Learning tab: assignments + items needing attention
  const totalLearningBadge = (needsAttentionCount || 0) + (learningAssignmentsCount || 0);
  const { currentNotification, dismissNotification, snoozeNotification } = useAdminNotifications();
  const { shouldShowTour, markTourComplete, dismissTour } = useOnboardingTour();
  const { data: helpSettings } = useHelpSettings();
  const { hasPitchDeckAccess } = usePitchDeckAccess();
  
  // Enable real-time connection notifications with toasts
  useConnectionNotifications();
  
  const location = useLocation();
  const navigate = useNavigate();
  const [supportPanelOpen, setSupportPanelOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showTour, setShowTour] = useState(false);
  const [installPromptEvent, setInstallPromptEvent] = useState<any>(null);
  const [canInstall, setCanInstall] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const avatarUrl = useStorageUrl("avatars", profile?.avatar_url);

  // Show onboarding tour for new users
  useEffect(() => {
    if (shouldShowTour && !showTour) {
      // Small delay to ensure layout is ready
      const timer = setTimeout(() => setShowTour(true), 500);
      return () => clearTimeout(timer);
    } else if (!shouldShowTour && showTour) {
      // Sync local state when shouldShowTour becomes false
      setShowTour(false);
    }
  }, [shouldShowTour, showTour]);

  // Redirect non-onboarded users to profile page ONLY after tour is complete
  // (has_seen_tour = true but is_onboarded = false)
  useEffect(() => {
    if (!profileLoading && profile && profile.has_seen_tour && !profile.is_onboarded && location.pathname !== "/profile") {
      navigate("/profile");
    }
  }, [profile, profileLoading, location.pathname, navigate]);

  // PWA install prompt detection
  useEffect(() => {
    const standalone = window.matchMedia('(display-mode: standalone)').matches;
    setIsStandalone(standalone);

    const handler = (e: Event) => {
      e.preventDefault();
      setInstallPromptEvent(e);
      setCanInstall(true);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleTourComplete = () => {
    markTourComplete.mutate();
    dismissTour();
    setShowTour(false);
    navigate("/profile");
  };

  const handleTourSkip = () => {
    markTourComplete.mutate();
    dismissTour();
    setShowTour(false);
    navigate("/profile");
  };

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  const handleInstallApp = async () => {
    if (installPromptEvent) {
      installPromptEvent.prompt();
      const { outcome } = await installPromptEvent.userChoice;
      if (outcome === 'accepted') {
        setCanInstall(false);
        setInstallPromptEvent(null);
      }
    } else {
      // For iOS or if prompt not available
      alert('To install on iOS: Tap the Share button and select "Add to Home Screen"');
    }
  };

  const { data: topShopApp } = useMyTopShopApplication();
  const hasTopShopApplication = !!topShopApp;
  const isAdmin = roles?.includes("admin") ?? false;
  const isInstructor = roles?.includes("instructor") ?? false;
  const isMentor = profile?.role === "mentor";

  const navigation = [
    { name: "Dashboard", href: "/dashboard", icon: LayoutDashboard, tourId: "dashboard" },
    ...(hasTopShopApplication ? [{ name: "Top Shop", href: "/topshop/hub", icon: Store }] : []),
    { name: "Directory", href: "/directory", icon: Users },
    { name: "My Profile", href: "/profile", icon: UserCircle, tourId: "profile" },
    // unreadCount was fetched every 30s but never rendered anywhere; surface it
    // as a badge now that the count is actually correct (it used to be
    // permanently 0 because it filtered on the always-null messages.connection_id).
    { name: "Connections", href: "/connections", icon: MessageSquare, tourId: "connections", badge: unreadCount && unreadCount > 0 ? unreadCount : undefined },
    { name: "Schedule", href: "/schedule", icon: Calendar },
    { name: "Announcements", href: "/announcements", icon: Megaphone },
    { name: "Events", href: "/events", icon: CalendarDays, tourId: "events" },
    { name: "Learning", href: "/curricula", icon: GraduationCap, tourId: "learning", badge: totalLearningBadge > 0 ? totalLearningBadge : undefined },
    { name: "Discussions", href: "/discussions", icon: MessageSquare },
    { name: "Resources", href: "/resources", icon: Library },
    { name: "Ideation Hub", href: "https://eab.sparkrockets.com", icon: ExternalLink, external: true },
  ];

  // Add Pitch Deck link when the user has feature access
  if (hasPitchDeckAccess) {
    navigation.push({ name: "Pitch Deck", href: "/pitch-deck", icon: Presentation } as typeof navigation[0]);
  }

  // Add Help link if enabled in settings
  // Add Help link if enabled in settings
  if (helpSettings?.show_in_navigation) {
    navigation.push({ name: "Help", href: "/help", icon: HelpCircle } as typeof navigation[0]);
  }

  if (isAdmin || isInstructor) {
    navigation.push({ name: "Review Queue", href: "/instructor/review-queue", icon: ClipboardCheck, badge: pendingReviewCount } as typeof navigation[0]);
    navigation.push({ name: "Instructor", href: "/instructor/courses", icon: GraduationCap } as typeof navigation[0]);
  }

  if (isAdmin) {
    navigation.push({ name: "Admin", href: "/admin", icon: Shield } as typeof navigation[0]);
    navigation.push({ name: "Top Shop Admin", href: "/admin/top-shop", icon: Store } as typeof navigation[0]);
    navigation.push({ name: "Roles & Access", href: "/admin/access", icon: KeyRound } as typeof navigation[0]);
  }

  return (
    <div className="min-h-screen bg-secondary">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-foreground/20 backdrop-blur-sm lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 w-72 sm:w-64 bg-card border-r transform transition-transform duration-200 ease-in-out lg:translate-x-0",
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="flex h-full flex-col">
          <div className="flex items-center justify-between h-14 sm:h-16 px-4 sm:px-6 border-b">
            <Link to="/dashboard" className="flex items-center">
              <span className="text-lg sm:text-xl font-bold text-primary">EAB Connect</span>
            </Link>
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden h-8 w-8"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          <nav className="flex-1 overflow-y-auto px-3 sm:px-4 py-4 space-y-1">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              const isExternal = 'external' in item && item.external;
              const isConnections = item.href === "/connections";
              const hasBadge = 'badge' in item && typeof item.badge === 'number' && item.badge > 0;
              
              if (isExternal) {
                return (
                  <a
                    key={item.name}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center px-3 py-3 sm:py-2.5 rounded-lg text-sm font-medium transition-colors text-muted-foreground hover:bg-muted hover:text-foreground active:bg-muted/80"
                    onClick={() => setSidebarOpen(false)}
                  >
                    <item.icon className="mr-3 h-5 w-5 flex-shrink-0" />
                    <span className="truncate">{item.name}</span>
                  </a>
                );
              }
              
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  data-tour={'tourId' in item ? item.tourId : undefined}
                  className={cn(
                    "flex items-center px-3 py-3 sm:py-2.5 rounded-lg text-sm font-medium transition-colors active:bg-muted/80",
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground"
                  )}
                  onClick={() => setSidebarOpen(false)}
                >
                  <item.icon className="mr-3 h-5 w-5 flex-shrink-0" />
                  <span className="flex-1 truncate">{item.name}</span>
                  {isConnections && newIndicator?.hasNew && (
                    <Badge 
                      className={cn(
                        "ml-2 h-5 px-1.5 flex items-center justify-center text-xs font-semibold flex-shrink-0",
                        isActive 
                          ? "bg-primary-foreground text-primary" 
                          : "bg-primary text-primary-foreground"
                      )}
                    >
                      NEW
                    </Badge>
                  )}
                  {hasBadge && (
                    <Badge 
                      className={cn(
                        "ml-2 h-5 min-w-5 px-1.5 flex items-center justify-center text-xs font-semibold flex-shrink-0",
                        isActive 
                          ? "bg-orange-500 text-white" 
                          : "bg-orange-500 text-white"
                      )}
                    >
                      {item.badge}
                    </Badge>
                  )}
                </Link>
              );
            })}
          </nav>

          <div className="p-3 sm:p-4 border-t">
            <div className="flex items-center gap-2 sm:gap-3 px-2 sm:px-3 py-2">
              <Avatar className="h-8 w-8 sm:h-9 sm:w-9 flex-shrink-0">
                <AvatarImage src={avatarUrl || undefined} />
                <AvatarFallback className="bg-primary text-primary-foreground">
                  {profile?.full_name?.charAt(0) || user?.email?.charAt(0)?.toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{profile?.full_name || "User"}</p>
                <p className="text-xs text-muted-foreground capitalize truncate">{profile?.role}</p>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="sticky top-0 z-30 h-14 sm:h-16 bg-card border-b flex items-center justify-between px-3 sm:px-4 lg:px-6">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden h-9 w-9"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </Button>

          <div className="flex-1" />

          <div className="flex items-center gap-2 sm:gap-3 lg:gap-4">
            {!profile?.is_published && (
              <>
                <span className="hidden sm:inline-flex text-xs bg-warning/10 text-warning px-2 py-1 rounded-full whitespace-nowrap">
                  Profile pending approval
                </span>
                <span className="sm:hidden text-xs bg-warning/10 text-warning px-2 py-1 rounded-full">
                  Pending
                </span>
              </>
            )}

            <NotificationsBell />
            <AskNedraButton onClick={() => setSupportPanelOpen(true)} />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-8 w-8 sm:h-9 sm:w-9 rounded-full">
                  <Avatar className="h-8 w-8 sm:h-9 sm:w-9">
                    <AvatarImage src={avatarUrl || undefined} />
                    <AvatarFallback className="bg-primary text-primary-foreground">
                      {profile?.full_name?.charAt(0) || user?.email?.charAt(0)?.toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium truncate">{profile?.full_name}</p>
                    <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/profile")}>
                  <UserCircle className="mr-2 h-4 w-4" />
                  My Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleInstallApp}>
                  <Download className="mr-2 h-4 w-4" />
                  Install App
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/settings")}>
                  <Settings className="mr-2 h-4 w-4" />
                  Settings
                </DropdownMenuItem>
                {helpSettings?.help_enabled && (
                  <DropdownMenuItem onClick={() => navigate("/help")}>
                    <HelpCircle className="mr-2 h-4 w-4" />
                    Help
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleSignOut} className="text-destructive">
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Pending survey banner (just below header) */}
        <SurveyPendingBanner />

        {/* Page content */}
        <main className="p-3 sm:p-4 lg:p-6">
          {children}
        </main>
      </div>

      {/* Pending survey modal (first-login per assignment) */}
      <SurveyAssignmentModal />

      {/* Admin Notification Popup */}
      <AdminNotificationDialog
        notification={currentNotification}
        onDismiss={dismissNotification}
        onSnooze={snoozeNotification}
      />

      {/* Support Panel */}
      <SupportPanel open={supportPanelOpen} onOpenChange={setSupportPanelOpen} />

      {/* Onboarding Tour */}
      <OnboardingTour
        isOpen={showTour}
        onComplete={handleTourComplete}
        onSkip={handleTourSkip}
      />
    </div>
  );
}
